import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { EXPORT_FESTIVAL } from "./graphql";
import { Button, Stack, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { BandBooking } from "../../__generated__/graphql";
import dayjs from "dayjs";
import BandsTables from "./tables";
import { toast } from "react-toastify";
import { PageContainer } from "../../components/PageContainer";
import { LoadingOverlay } from "../../components/Loading";
import { GET_ANNOUNCEMENT } from "../announcement/graphql";
import { LinkButton } from "../../components/LinkButton";
import { useIsSuperAdmin } from "../../App";
import { CREATE_BOOKING, DELETE_BOOKING } from "../booking/graphql";
import { confirmAlert } from "react-confirm-alert";
import BandSearchDropdown from "./BandSearchDropdown";
import {
  DayOption,
  ScheduleDaySelector,
} from "../../components/ScheduleDaySelector";

const ALL_DAYS_KEY = "__ALL_DAYS__";

const AnnouncementBands = () => {
  const { announcementId } = useParams();
  const { data, error, loading } = useQuery(GET_ANNOUNCEMENT, {
    variables: {
      id: announcementId!,
    },
    fetchPolicy: "cache-first",
  });

  const [bands, setBands] = useState<Map<string, BandBooking[]>>(
    new Map<string, BandBooking[]>()
  );
  const [selectedDate, setSelectedDate] = useState<string | undefined>(
    ALL_DAYS_KEY
  );
  const [selectedBand, setSelectedBand] = useState<string | undefined>();
  const [removingBookingIds, setRemovingBookingIds] = useState<Set<string>>(
    new Set()
  );
  const navigate = useNavigate();
  const isSuperAdmin = useIsSuperAdmin();

  const [deleteBooking] = useMutation(DELETE_BOOKING, {
    refetchQueries: () =>
      announcementId
        ? [{ query: GET_ANNOUNCEMENT, variables: { id: announcementId } }]
        : [],
    awaitRefetchQueries: true,
    onCompleted: () => {
      toast("Booking removed from announcement");
    },
    onError: (mutationError) => {
      toast.error(mutationError.message);
    },
  });
  const [createBooking, { loading: creatingBooking }] = useMutation(
    CREATE_BOOKING,
    {
      refetchQueries: () =>
        announcementId
          ? [{ query: GET_ANNOUNCEMENT, variables: { id: announcementId } }]
          : [],
      awaitRefetchQueries: true,
      onCompleted: () => {
        toast("Band added to announcement");
        setSelectedBand(undefined);
      },
      onError: (mutationError) => {
        toast.error(mutationError.message);
      },
    }
  );

  useEffect(() => {
    if (!data?.announcement?.bands) return;

    const bandsPerDay: Map<string, BandBooking[]> =
      data.announcement?.bands.reduce((prev, next) => {
        const date = dayjs(next.startTime).utc(true);

        let effectDate = date.format("YYYY/MM/DD");

        if (date.hour() < 5) {
          effectDate = date.subtract(1, "day").format("YYYY/MM/DD");
        }

        const key = date.isValid() ? effectDate : "No Date";

        const bands = prev.get(key) ?? [];

        prev.set(key, [...bands, next]);

        return prev;
      }, new Map());
    setBands(bandsPerDay);
  }, [data]);

  const [exportFestival, { loading: updateLoading }] = useLazyQuery(
    EXPORT_FESTIVAL,
    {
      onCompleted: (data) => {
        console.log("data", data);
        toast("App updated");
        setTimeout(
          () => navigate(`/notifications/${data.clashfinderExport}`),
          2000
        );
      },
      onError: () => {
        toast("Error updating lineup");
      },
    }
  );

  const handleSelectDay = (selectedDate: string) => {
    setSelectedDate(selectedDate);
  };

  const existingBandIds = useMemo(
    () =>
      Array.from(
        new Set(
          (data?.announcement?.bands ?? [])
            .map((booking) => booking?.band?.id)
            .filter((id): id is string => Boolean(id))
        )
      ),
    [data?.announcement?.bands]
  );

  const handleAddSelectedBand = () => {
    if (!selectedBand) return;
    if (!announcementId) return;

    if (existingBandIds.includes(selectedBand)) {
      toast.error("This band is already in the announcement.");
      return;
    }

    createBooking({
      variables: {
        bandId: selectedBand,
        announcementId,
        stageId: null,
        startTime: null,
        endTime: null,
        date: null,
        editionId: null,
      },
    });
  };

  const handleRemoveBooking = (bookingId: string, bandName: string) => {
    if (!bookingId) {
      toast.error("Unable to remove booking: missing booking id.");
      return;
    }

    if (removingBookingIds.has(bookingId)) return;

    confirmAlert({
      title: "Remove booking from announcement?",
      message: `Remove ${bandName} from this announcement?`,
      buttons: [
        {
          label: "Remove booking",
          onClick: async () => {
            setRemovingBookingIds((prev) => new Set(prev).add(bookingId));

            try {
              await deleteBooking({ variables: { bookingId } });
            } finally {
              setRemovingBookingIds((prev) => {
                const next = new Set(prev);
                next.delete(bookingId);
                return next;
              });
            }
          },
        },
        {
          label: "Cancel",
          onClick: () => {},
        },
      ],
    });
  };

  const keys = useMemo(() => Array.from(bands.keys()), [bands]);
  const sortedKeys = useMemo(
    () =>
      [...keys].sort((a, b) => {
        const aDate = dayjs(a);
        const bDate = dayjs(b);
        const aValue = aDate.isValid()
          ? aDate.valueOf()
          : Number.MAX_SAFE_INTEGER;
        const bValue = bDate.isValid()
          ? bDate.valueOf()
          : Number.MAX_SAFE_INTEGER;
        return aValue - bValue;
      }),
    [keys]
  );

  useEffect(() => {
    if (sortedKeys.length === 0) return;

    if (!selectedDate) {
      setSelectedDate(ALL_DAYS_KEY);
      return;
    }

    if (selectedDate !== ALL_DAYS_KEY && !sortedKeys.includes(selectedDate)) {
      setSelectedDate(ALL_DAYS_KEY);
    }
  }, [sortedKeys, selectedDate]);

  const defaultDate = sortedKeys[0];
  const selectedBands = useMemo(() => {
    if (selectedDate === ALL_DAYS_KEY) {
      return Array.from(bands.values()).flat();
    }

    const key = selectedDate ?? defaultDate;
    if (!key) return undefined;

    return bands.get(key);
  }, [bands, defaultDate, selectedDate]);
  const dayOptions: DayOption[] = useMemo(() => {
    const options: DayOption[] = [];

    if (sortedKeys.length > 0) {
      options.push({
        key: ALL_DAYS_KEY,
        title: "All Days",
        subtitle: `${sortedKeys.length} day${sortedKeys.length === 1 ? "" : "s"}`,
      });
    }

    sortedKeys.forEach((item) => {
      const isValid = dayjs(item).isValid();
      options.push({
        key: item,
        title: isValid ? dayjs(item).format("dddd DD") : "No Date",
        subtitle: isValid ? dayjs(item).format("YYYY/MM/DD") : "-",
      });
    });

    return options;
  }, [sortedKeys]);

  const selectedDayKey = selectedDate ?? defaultDate ?? dayOptions[0]?.key;

  if (loading) return <LoadingOverlay loading={loading} />;

  if (!data?.announcement) return <div>announcement not found</div>;

  const availabilityLabel = data?.announcement?.availabilityDate
    ? dayjs(data.announcement.availabilityDate).format("YYYY/MM/DD HH:mm")
    : null;

  return (
    <LoadingOverlay loading={loading}>
      <PageContainer
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span>{data?.announcement?.name ?? ""}</span>
            {availabilityLabel && (
              <span
                style={{
                  padding: "4px 10px",
                  borderRadius: 999,
                  border: "1px solid rgba(0,0,0,0.1)",
                  background: "rgba(0,0,0,0.04)",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "rgba(0,0,0,0.8)",
                }}
              >
                Available: {availabilityLabel}
              </span>
            )}
          </div>
        }
        actions={
          data?.announcement ? (
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={() => {
                  navigate("/booking/create", {
                    state: {
                      announcementId: announcementId,
                      festivalId: data?.announcement?.edition?.id ?? "",
                    },
                  });
                }}
              >
                Add Band Booking
              </Button>
              {isSuperAdmin && (
                <LinkButton
                  text="View in app"
                  path={`ltd.meliora.${process.env.REACT_APP_BUNDLE_ID}://switch/${data.announcement.id}`}
                  variant="outlined"
                />
              )}
              <LinkButton
                path={`/edition/${data.announcement.edition?.id}`}
                text="View Edition"
                variant="outlined"
              />
              <LinkButton
                path={`/announcement/edit/${data.announcement.edition?.id}/${data.announcement.id}`}
                text="Edit Announcement"
                variant="outlined"
              />
            </Stack>
          ) : undefined
        }
      >
        {dayOptions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No bookings found for this announcement.
          </Typography>
        ) : (
          <ScheduleDaySelector
            days={dayOptions}
            selectedKey={selectedDayKey}
            onSelect={handleSelectDay}
          />
        )}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
          sx={{ mt: 4 }}
        >
          <BandSearchDropdown
            value={selectedBand}
            onChange={setSelectedBand}
            placeholder="Select a band"
            controlStyle={{ maxWidth: 420, marginTop: 0, marginBottom: 0 }}
            except={existingBandIds}
          />
          <Button
            variant="contained"
            disabled={!selectedBand || !announcementId || creatingBooking}
            onClick={handleAddSelectedBand}
          >
            {creatingBooking ? "Adding..." : "Add Booking"}
          </Button>
        </Stack>
        {selectedBands && (
          <BandsTables
            bands={selectedBands}
            eventTimezone={data?.announcement?.edition?.timezone!}
            onRemoveBooking={handleRemoveBooking}
            removingBookingIds={removingBookingIds}
          />
        )}
      </PageContainer>
    </LoadingOverlay>
  );
};

export default AnnouncementBands;
